import {
  clampLandingIntroHeightRatio,
  LANDING_INTRO_DEFAULT_HEIGHT_RATIO,
  parseIntroBlockContent,
  parseIntroSectionKind,
  type LandingIntroSection,
} from '../types/landingIntro';
import type { LandingFaqItem } from '../types/landingFaq';
import type { LandingPageSection } from '../types/landingPage';
import { parseSlideLayoutJson } from '../types/programContentSlideCanvas';

export const LANDING_PUBLISHED_SNAPSHOT_VERSION = 1 as const;
export const LANDING_PUBLISHED_SNAPSHOT_PATH = '/landing-published/snapshot.json';

export type LandingPublishedSnapshotV1 = {
  version: typeof LANDING_PUBLISHED_SNAPSHOT_VERSION;
  publishedAt: string;
  sections: LandingPageSection[];
  intro: LandingIntroSection[];
  faq: LandingFaqItem[];
};

function asSection(raw: unknown): LandingPageSection | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.section_key !== 'string') return null;
  return {
    id: o.id,
    section_key: o.section_key,
    section_order: Number(o.section_order ?? 0),
    title: typeof o.title === 'string' ? o.title : '',
    subtitle: typeof o.subtitle === 'string' ? o.subtitle : null,
    body: typeof o.body === 'string' ? o.body : null,
    cta_label: typeof o.cta_label === 'string' ? o.cta_label : null,
    cta_link: typeof o.cta_link === 'string' ? o.cta_link : null,
    image_path: typeof o.image_path === 'string' ? o.image_path : null,
    image_url: typeof o.image_url === 'string' ? o.image_url : null,
    image_version: typeof o.image_version === 'number' ? o.image_version : 1,
    is_active: o.is_active !== false,
    created_at: typeof o.created_at === 'string' ? o.created_at : '',
    updated_at: typeof o.updated_at === 'string' ? o.updated_at : '',
  };
}

function asIntro(raw: unknown): LandingIntroSection | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string') return null;
  const section_kind = parseIntroSectionKind(o.section_kind);
  return {
    id: o.id,
    sort_order: Number(o.sort_order ?? 0),
    title: '',
    section_kind,
    layout_json: parseSlideLayoutJson(o.layout_json),
    content_json: parseIntroBlockContent(section_kind, o.content_json),
    height_ratio: clampLandingIntroHeightRatio(
      typeof o.height_ratio === 'number' ? o.height_ratio : LANDING_INTRO_DEFAULT_HEIGHT_RATIO
    ),
    is_active: o.is_active !== false,
    created_at: typeof o.created_at === 'string' ? o.created_at : '',
    updated_at: typeof o.updated_at === 'string' ? o.updated_at : '',
  };
}

function asFaq(raw: unknown): LandingFaqItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string') return null;
  return {
    id: o.id,
    sort_order: Number(o.sort_order ?? 0),
    category: typeof o.category === 'string' ? o.category : '',
    question: typeof o.question === 'string' ? o.question : '',
    answer: typeof o.answer === 'string' ? o.answer : '',
    is_active: o.is_active !== false,
    created_at: typeof o.created_at === 'string' ? o.created_at : '',
    updated_at: typeof o.updated_at === 'string' ? o.updated_at : '',
  };
}

export function parseLandingPublishedSnapshot(raw: unknown): LandingPublishedSnapshotV1 | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.version !== LANDING_PUBLISHED_SNAPSHOT_VERSION) return null;
  if (!Array.isArray(o.sections) || !Array.isArray(o.intro) || !Array.isArray(o.faq)) return null;

  const sections = o.sections.map(asSection).filter((row): row is LandingPageSection => Boolean(row));
  const intro = o.intro.map(asIntro).filter((row): row is LandingIntroSection => Boolean(row));
  const faq = o.faq.map(asFaq).filter((row): row is LandingFaqItem => Boolean(row));

  return {
    version: LANDING_PUBLISHED_SNAPSHOT_VERSION,
    publishedAt: typeof o.publishedAt === 'string' ? o.publishedAt : '',
    sections: sections
      .filter((row) => row.is_active)
      .sort((a, b) => a.section_order - b.section_order),
    intro: intro.filter((row) => row.is_active).sort((a, b) => a.sort_order - b.sort_order),
    faq: faq.filter((row) => row.is_active).sort((a, b) => a.sort_order - b.sort_order),
  };
}

export const LANDING_PUBLISHED_CACHE_KEY = 'curriculum-landing-published.v1';

export function isLandingPublishedMode(): boolean {
  return process.env.NODE_ENV === 'production';
}

let snapshotMemory: LandingPublishedSnapshotV1 | null | undefined;

export function resetLandingPublishedSnapshotMemory(): void {
  snapshotMemory = undefined;
}

export function loadCachedPublishedSnapshot(): LandingPublishedSnapshotV1 | null {
  try {
    const raw = localStorage.getItem(LANDING_PUBLISHED_CACHE_KEY);
    if (!raw) return null;
    return parseLandingPublishedSnapshot(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function savePublishedSnapshotCache(snapshot: LandingPublishedSnapshotV1): void {
  try {
    localStorage.setItem(LANDING_PUBLISHED_CACHE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
}

export async function loadLandingPublishedSnapshot(): Promise<LandingPublishedSnapshotV1 | null> {
  if (snapshotMemory !== undefined) return snapshotMemory;

  const cached = loadCachedPublishedSnapshot();
  if (cached) snapshotMemory = cached;

  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    const res = await fetch(`${publicUrl}${LANDING_PUBLISHED_SNAPSHOT_PATH}`, {
      cache: 'default',
    });
    if (!res.ok) {
      snapshotMemory = cached ?? null;
      return snapshotMemory;
    }
    const next = parseLandingPublishedSnapshot(await res.json());
    if (!next) {
      snapshotMemory = cached ?? null;
      return snapshotMemory;
    }
    if (cached?.publishedAt && cached.publishedAt === next.publishedAt) {
      snapshotMemory = cached;
      return cached;
    }
    savePublishedSnapshotCache(next);
    snapshotMemory = next;
    return next;
  } catch {
    snapshotMemory = cached ?? null;
    return snapshotMemory;
  }
}
